
import { supabase } from '@/integrations/supabase/client';

interface RealtimeConfig {
  event: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
  schema: string;
  table: string;
  filter?: string;
}

interface RealtimePayload {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: Record<string, unknown>;
  old: Record<string, unknown>;
  errors: string[] | null;
}

type RealtimeCallback = (payload?: RealtimePayload) => void;

class SubscriptionManager {
  private channels = new Map<string, any>();
  private debounceTimers = new Map<string, NodeJS.Timeout>();
  private defaultDebounceMs = 1000;

  /**
   * Create a realtime channel with automatic debouncing
   * @param channelName - Unique name for the channel
   * @param config - Realtime configuration (event, schema, table, filter)
   * @param callback - Function to call when changes occur
   * @param options - Additional options (debounceMs, skipDebounce for INSERT events)
   */
  createChannel(
    channelName: string, 
    config: RealtimeConfig, 
    callback: RealtimeCallback,
    options?: { debounceMs?: number; skipDebounceOnInsert?: boolean }
  ) {
    // Clean up existing channel with the same name
    this.removeChannel(channelName);

    const debounceMs = options?.debounceMs ?? this.defaultDebounceMs;
    const skipDebounceOnInsert = options?.skipDebounceOnInsert ?? false;

    console.log(`📡 Creating channel: ${channelName}`);
    
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', config, (payload) => {
        const realtimePayload = payload as unknown as RealtimePayload;
        console.log(`📡 ${channelName} table changed (${realtimePayload.eventType}), processing...`);
        
        // For INSERT events with skipDebounceOnInsert, call immediately
        if (realtimePayload.eventType === 'INSERT' && skipDebounceOnInsert) {
          console.log(`📡 ${channelName} INSERT detected, calling immediately`);
          callback(realtimePayload);
          return;
        }
        
        // Debounce the callback to prevent excessive calls
        const existingTimer = this.debounceTimers.get(channelName);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }
        
        const timer = setTimeout(() => {
          console.log(`📡 ${channelName} executing debounced callback`);
          callback(realtimePayload);
          this.debounceTimers.delete(channelName);
        }, debounceMs);
        
        this.debounceTimers.set(channelName, timer);
      })
      .subscribe();

    this.channels.set(channelName, channel);
    return channel;
  }

  removeChannel(channelName: string) {
    const existingChannel = this.channels.get(channelName);
    if (existingChannel) {
      console.log(`🧹 Cleaning up channel: ${channelName}`);
      supabase.removeChannel(existingChannel);
      this.channels.delete(channelName);
      
      // Clear any pending debounce timer
      const timer = this.debounceTimers.get(channelName);
      if (timer) {
        clearTimeout(timer);
        this.debounceTimers.delete(channelName);
      }
    }
  }

  removeAllChannels() {
    console.log('🧹 Cleaning up all channels');
    this.channels.forEach((channel, name) => {
      supabase.removeChannel(channel);
    });
    this.channels.clear();
    
    // Clear all debounce timers
    this.debounceTimers.forEach((timer) => {
      clearTimeout(timer);
    });
    this.debounceTimers.clear();
  }

  getChannelCount() {
    return this.channels.size;
  }
}

export const subscriptionManager = new SubscriptionManager();
